import { Injectable, OnDestroy } from '@angular/core';
import { loginStoreActions, relationshipStoreActions, RootStoreState } from '@app/root-store';
import { loaderTypes } from '@app/shared/component/loader/loader';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, forkJoin, from, Observable, of, Subject } from 'rxjs';
import { concatMap, finalize, map, take, takeUntil, toArray } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { AmplifyHttpClient } from '../auth/http.amplify-httpclient';
import { GraphQLService } from '../graphql/graphql.service';
import * as loginSelectors from '../../../root-store/login-store/login.selector';

import { environment } from '@env/environment';
import { fetchAuthSession, fetchUserAttributes } from '@aws-amplify/auth';
import { LoaderService } from '../loader/loader.service';
@Injectable({
  providedIn: 'root',
})
export class UserConnectionService implements OnDestroy {
  currentUserDetails: any;
  private worker: Worker | null = null;
  private activeRequests: Map<string, Promise<any>> = new Map();
  private activeConnectionRequests: Set<string> = new Set();
  private destroy$ = new Subject<void>();

  constructor(
    private readonly httpService: AmplifyHttpClient,
    private store$: Store<RootStoreState.State>,
    private readonly graphqlService: GraphQLService,
    private authService: AuthService,
    private store: Store,
    private loaderService: LoaderService
  ) {
    // FIXED: Using takeUntil to properly cleanup subscription
    this.store.pipe(select(loginSelectors.getCurrentUserFullDetails), takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.currentUserDetails = result;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this._connections.complete();
    this.activeRequests.clear();
    this.activeConnectionRequests.clear();
  }

  private _connections = new BehaviorSubject<Array<any>>([]);
  public connections$ = this._connections.asObservable();
  public set connections(value: any) {
    this._connections.next(value);
  }
  /**
   * userConnection method for get connection of loggedin users.
   * @param user_id
   * @return Observable
   */
  connectToUser(request, loader?: loaderTypes) {
    // const addConnectionRequest = {
    //   from_user_id: request.from_user_id ?? this.authService.user.id,
    //   to_user_id: request.requested_user_id,
    //   status: request.status ?? 'pending',
    //   relationship_id: request.relationship_id,
    // };

    const addConnectionRequest = {
      from_user_id: request.from_user_id ?? this.currentUserDetails.id,
      to_user_id: request.requested_user_id,
      status: request.status ?? 'pending',
      relationship_id: request.relationship_id,
    };

    return this.graphqlService.addConnection(addConnectionRequest, loader).pipe(
      map((response) => {
        if (response.status == 200) {
          this.store$.dispatch(new relationshipStoreActions.AllRelationships());
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  userConnectionUpdate(request) {
    //return this.httpService.post<any>(`api/connection/connect-user-update`, request).pipe(
    // request.from_user_id = this.authService.user.id;
    return this.graphqlService.updateConnection(request, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipUpdateNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-update-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  deleteUserConnection(request) {
    return this.graphqlService.dropConnection(request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  sendRelationshipReminderNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-reminder-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipDeleteNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-delete-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipRequestDeclineNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-request-decline-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipRequestAcceptNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-request-accept-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          this.store$.dispatch(new loginStoreActions.LoginRequestAction());
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendRelationshipRequestNotification(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-request-notification`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  sendInviteReminderKinnectEmail(request) {
    return this.httpService.post<any>(`api/connection/send-relationship-reminder`, request, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  sendInviteReminderOutsideKinnectEmail(request) {
    return this.httpService.post<any>(`api/user/send-invite-reminder-email`, request, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  deleteUserInviteOutsideKinnectEmail(request) {
    return this.httpService.post<any>(`api/user/delete-invite`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getMutualConnectionCount(id) {
    return this.httpService.get<any>(`api/connection/mutual-connections-count/${id}`).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getMutualConnectionList(id) {
    return this.httpService.post<any>(`api/connection/mutual-connections/${id}`, {}).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getMutualConnection(request, user_id) {
    return this.httpService.post<any>(`api/connection/mutual-connections/` + user_id, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  /**
   * @deprecated The method should not be used as it's been replaced with getallconnections with ngrx
   */
  getConnections(type = 'user', params = {}) {
    return this.graphqlService.getConnections('').pipe(
      map((response) => {
        if (response.status == 200) {
          this.connections = response.data;
          return response.data;
        } else {
          this.connections = response;
          return response;
        }
      })
    );
  }
  getSuggestionsWithLimit(count) {
    return this.httpService.get<any>(`api/connection/my-suggestion-limit/${count}`, loaderTypes.none).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getSuggestions(request) {
    return this.httpService.post<any>(`api/connection/suggestion`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getSuggestionsWithPagination(pageNo) {
    return this.httpService.get<any>(`api/connection/my-suggestion-all/${pageNo}`).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getConnectionRequest(request) {
    return this.httpService
      .get<any>(`api/connection/all-request`, { params: request })
      .pipe(
        map((response) => {
          return response;
        })
      )
      .toPromise()
      .then((data: any) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });
    // return this.httpService.get<any>(``, request).pipe(

    // )
  }
  getRequestConnectionStatus(request) {
    return this.httpService
      .get<any>(`api/connection/request-status-change`, { params: request })
      .pipe(
        map((response) => {
          return response;
        })
      )
      .toPromise()
      .then((data: any) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });
  }

  userConnectionUpdateStatus(request) {
    return this.httpService.post<any>(`api/connection/request-status-change`, request).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getRelationshipChangeRequestStatus(request) {
    return this.httpService
      .get<any>(`api/connection/get-relationship-change-request-status`, { params: request })
      .pipe(
        map((response) => {
          return response;
        })
      )
      .toPromise()
      .then((data: any) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });
  }

  getConnectionsCount(user_id) {
    return this.httpService.get<any>(`api/connection/get-connection-count`, { params: user_id }).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  ignoreSuggestion(suggested_user_id) {
    return this.httpService.post<any>(`api/connection/suggestion-ignore`, { suggested_user_id: suggested_user_id }).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getAllConnections(params) {
    return this.graphqlService.getConnections(params).pipe(
      map((response) => {
        if (response.status == 200) {
          this.connections = response.data;
          return response.data;
        } else {
          this.connections = response;
        }
      })
    );
  }

  checkUserIsAlreadyintheSystem(data) {
    return this.httpService.post<any>(`api/user/get-byEmail`, data, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  async getMySuggestions(): Promise<any> {
    const requestKey = 'getMySuggestions'; // Unique key for this request type

    // Check if there is an already active request
    if (this.activeRequests.has(requestKey)) {
      return this.activeRequests.get(requestKey)!;
    }

    // Create a new promise for the current request
    const requestPromise = new Promise(async (resolve, reject) => {
      if (typeof Worker !== 'undefined') {
        if (!this.worker) {
          this.worker = new Worker(new URL('./my-suggestion.worker', import.meta.url), { type: 'module' });
        }

        let data = {
          token: '',
          url: '',
        };

        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          data.token = `Bearer ${token}`;
          data.url = `${environment.apiUrl}/api/connection/my-suggestions`;

          this.worker.onmessage = ({ data }) => {
            const res = data.data;
            resolve(res);

            // Cleanup: Remove the active request after it's completed
            this.activeRequests.delete(requestKey);
          };

          this.worker.onerror = (error) => {
            reject(error);

            // Cleanup on error
            this.activeRequests.delete(requestKey);
          };

          this.worker.postMessage(data);
        } catch (error) {
          reject(error);
          this.activeRequests.delete(requestKey);
        }
      } else {
        // Browser does not support web workers, handle fallback logic
        // resolve(this.generateCreatePresignedUrl(null));
        reject(new Error('Web Workers are not supported in this browser.'));
        this.activeRequests.delete(requestKey);
      }
    });

    // Store the active request in the map
    this.activeRequests.set(requestKey, requestPromise);

    return requestPromise;
  }

  getMutualConnections(user_id) {
    return this.httpService.get<any>(`api/connection/mutual-connections/${user_id}`, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  mySuggestionRemove(suggested_user_id) {
    return this.httpService.post<any>(`api/connection/my-suggestions-ignore`, { suggested_user_list: suggested_user_id }).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  /**
   * Internal method to add connection without dispatching AllRelationships action
   * Used for batch operations to avoid multiple dispatches
   * Includes duplicate call prevention at GraphQL level
   */
  private addConnectionInternal(request, loader?: loaderTypes): Observable<any> {
    const fromUserId = request.from_user_id ?? this.currentUserDetails.id;
    const toUserId = request.requested_user_id;
    
    // Normalize the connection direction to prevent bidirectional duplicates
    const normalized = this.normalizeConnectionDirection(fromUserId, toUserId);
    
    const addConnectionRequest = {
      from_user_id: normalized.from_user_id,
      to_user_id: normalized.to_user_id,
      status: request.status ?? 'pending',
      relationship_id: request.relationship_id,
    };

    // Create a unique key using normalized connection parameters to prevent duplicate GraphQL calls
    // This ensures A->B and B->A are treated as the same connection
    const requestKey = `${normalized.from_user_id}_${normalized.to_user_id}_${addConnectionRequest.relationship_id}_${addConnectionRequest.status}`;
    
    // Check if this exact connection request is already in progress
    if (this.activeConnectionRequests.has(requestKey)) {
      console.warn(`Duplicate connection request prevented: ${requestKey}`);
      return of(null); // Return null observable to skip duplicate
    }

    // Mark this request as active
    this.activeConnectionRequests.add(requestKey);

    return this.graphqlService.addConnection(addConnectionRequest, loader).pipe(
      map((response) => {
        // Remove from active requests on success
        this.activeConnectionRequests.delete(requestKey);
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      }),
      catchError((error) => {
        // Remove from active requests on error
        this.activeConnectionRequests.delete(requestKey);
        throw error;
      })
    );
  }

  /**
   * Creates a unique key for a connection pair to prevent duplicates
   * Always uses sorted IDs to ensure consistent direction
   */
  private getConnectionKey(fromUserId: string | number, toUserId: string | number): string {
    // Sort IDs to ensure bidirectional connections are treated as duplicates
    const ids = [String(fromUserId), String(toUserId)].sort();
    return `${ids[0]}_${ids[1]}`;
  }

  /**
   * Ensures connection is always created from lower ID to higher ID
   * This prevents bidirectional duplicates (A->B and B->A)
   */
  private normalizeConnectionDirection(fromUserId: string | number, toUserId: string | number): { from_user_id: string | number; to_user_id: string | number } {
    const fromId = String(fromUserId);
    const toId = String(toUserId);
    
    // Always create connection from lower ID to higher ID (lexicographic comparison)
    // This ensures consistent direction regardless of input order
    if (fromId < toId) {
      return { from_user_id: fromUserId, to_user_id: toUserId };
    } else if (fromId > toId) {
      return { from_user_id: toUserId, to_user_id: fromUserId };
    } else {
      // Same user ID - shouldn't happen, but handle it
      return { from_user_id: fromUserId, to_user_id: toUserId };
    }
  }

  /**
   * Creates a unique key from normalized connection parameters
   * Used to prevent duplicate addConnection calls
   */
  private getNormalizedConnectionKey(fromUserId: string | number, toUserId: string | number): string {
    const normalized = this.normalizeConnectionDirection(fromUserId, toUserId);
    return `${normalized.from_user_id}_${normalized.to_user_id}`;
  }

  connectMultipleUsers(userIds: Array<string | number>, loader?: loaderTypes) {
    this.loaderService.show(loaderTypes.mainAnimation);
    
    // Remove duplicate user IDs from input array first
    const uniqueUserIds = Array.from(new Set(userIds.map(id => String(id)))).map(id => {
      // Preserve original type (string or number)
      const originalId = userIds.find(origId => String(origId) === id);
      return originalId !== undefined ? originalId : id;
    });
    
    const connectionRequests: Array<{
      relationship_name?: string;
      from_user_id: string | number;
      requested_user_id: string | number;
      relationship_id: number;
      status?: string;
    }> = [];
    const seenConnections = new Set<string>();

    // Build connection requests and filter duplicates
    for (let i = 0; i < uniqueUserIds.length; i++) {
      for (let j = i + 1; j < uniqueUserIds.length; j++) {
        // Skip if same user
        if (String(uniqueUserIds[i]) === String(uniqueUserIds[j])) {
          continue;
        }
        
        // Use normalized connection key to prevent duplicates
        const connectionKey = this.getNormalizedConnectionKey(uniqueUserIds[i], uniqueUserIds[j]);
        
        // Skip if this connection pair already exists
        if (seenConnections.has(connectionKey)) {
          continue;
        }
        
        seenConnections.add(connectionKey);
        
        // Normalize direction: always from lower ID to higher ID
        const normalized = this.normalizeConnectionDirection(uniqueUserIds[i], uniqueUserIds[j]);
        
        connectionRequests.push({
          relationship_name: undefined,
          from_user_id: normalized.from_user_id,
          requested_user_id: normalized.to_user_id,
          relationship_id: 118,
          status: 'approved',
        });
      }
    }

    if (connectionRequests.length === 0) {
      return of([]).pipe(
        finalize(() => {
          this.loaderService.hide();
        })
      );
    }

    // Final deduplication: filter out any duplicate connections using normalized keys
    const finalConnectionRequests: typeof connectionRequests = [];
    const finalSeenConnections = new Set<string>();
    
    for (const connectionData of connectionRequests) {
      const normalizedKey = `${connectionData.from_user_id}_${connectionData.requested_user_id}_${connectionData.relationship_id}_${connectionData.status}`;
      if (!finalSeenConnections.has(normalizedKey)) {
        finalSeenConnections.add(normalizedKey);
        finalConnectionRequests.push(connectionData);
      }
    }

    return from(finalConnectionRequests).pipe(
      concatMap((connectionData) =>
        this.addConnectionInternal(connectionData, loaderTypes.mainAnimation).pipe(
          take(1),
          catchError((err) => {
            console.error(`Error connecting ${connectionData.from_user_id} -> ${connectionData.requested_user_id}`, err);
            return of(null); // Continue sequence on error
          })
        )
      ),
      toArray(),
      map((results) => {
        // Dispatch AllRelationships only once after all connections are complete
        this.store$.dispatch(new relationshipStoreActions.AllRelationships());
        return results;
      }),
      finalize(() => {
        this.loaderService.hide();
      })
    );
  }

  connectSingleUserToOtherUsers(selectedUserId: string | number, userIds: Array<string | number>, loader?: loaderTypes) {
    this.loaderService.show(loaderTypes.mainAnimation);
    
    // Remove duplicate user IDs from input array first
    const uniqueUserIds = Array.from(new Set(userIds.map(id => String(id)))).map(id => {
      // Preserve original type (string or number)
      const originalId = userIds.find(origId => String(origId) === id);
      return originalId !== undefined ? originalId : id;
    });
    
    const connectionRequests: Array<{
      relationship_name?: string;
      from_user_id: string | number;
      requested_user_id: string | number;
      relationship_id: number;
      status?: string;
    }> = [];
    const seenConnections = new Set<string>();

    // Build connection requests and filter duplicates
    for (let i = 0; i < uniqueUserIds.length; i++) {
      // Skip if same as selected user
      if (String(selectedUserId) === String(uniqueUserIds[i])) {
        continue;
      }
      
      // Use normalized connection key to prevent duplicates
      const connectionKey = this.getNormalizedConnectionKey(selectedUserId, uniqueUserIds[i]);
      
      // Skip if this connection pair already exists
      if (seenConnections.has(connectionKey)) {
        continue;
      }
      
      seenConnections.add(connectionKey);
      
      // Normalize direction: always from lower ID to higher ID
      // This ensures if user A connects to B, B won't create a reverse connection
      const normalized = this.normalizeConnectionDirection(selectedUserId, uniqueUserIds[i]);
      
      connectionRequests.push({
        relationship_name: undefined,
        from_user_id: normalized.from_user_id,
        requested_user_id: normalized.to_user_id,
        relationship_id: 118,
        status: 'approved',
      });
    }

    if (connectionRequests.length === 0) {
      return of([]).pipe(
        finalize(() => {
          this.loaderService.hide();
        })
      );
    }

    // Final deduplication: filter out any duplicate connections using normalized keys
    const finalConnectionRequests: typeof connectionRequests = [];
    const finalSeenConnections = new Set<string>();
    
    for (const connectionData of connectionRequests) {
      const normalizedKey = `${connectionData.from_user_id}_${connectionData.requested_user_id}_${connectionData.relationship_id}_${connectionData.status}`;
      if (!finalSeenConnections.has(normalizedKey)) {
        finalSeenConnections.add(normalizedKey);
        finalConnectionRequests.push(connectionData);
      }
    }

    return from(finalConnectionRequests).pipe(
      concatMap((connectionData) =>
        this.addConnectionInternal(connectionData, loaderTypes.mainAnimation).pipe(
          take(1),
          catchError((err) => {
            console.error(`Error connecting ${connectionData.from_user_id} -> ${connectionData.requested_user_id}`, err);
            return of(null); // Continue sequence on error
          })
        )
      ),
      toArray(),
      map((results) => {
        // Dispatch AllRelationships only once after all connections are complete
        this.store$.dispatch(new relationshipStoreActions.AllRelationships());
        return results;
      }),
      finalize(() => {
        this.loaderService.hide();
      })
    );
  }
  getRelationshipConnections(relationship_id: number) {
    return this.graphqlService.getConnections(relationship_id, loaderTypes.mainAnimation, true).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
  getUserInfo() {
    return this.httpService.get<any>(`api/user/connected-users-info`, loaderTypes.mainAnimation).pipe(
      map((response) => {
        if (response.status == 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }
}
